
import { defineComponent, computed } from 'vue'
import { store } from '@/store'
import type { LmsModule, CourseLesson } from '@/store/lmsCourse/index'

export default defineComponent({
  name: 'LmsModuleView',

  data() {
    return {
      loading: true,
    }
  },

  computed: {
    moduleId(): string {
      return this.$route.params.moduleId as string
    },
    module(): LmsModule | null {
      return store.getters['lmsCourse/getModuleById'](this.moduleId)
    },
    userId(): string {
      return store.getters['getUserId'] ?? 'anon'
    },
    lessons(): CourseLesson[] {
      return store.getters['lmsCourse/getLessons'](this.moduleId)
    },
    completedCount(): number {
      return store.getters['lmsCourse/completedLessonCount'](this.moduleId)
    },
    inProgressCount(): number {
      return store.getters['lmsCourse/inProgressLessonCount'](this.moduleId)
    },
    /**
     * Gesamtfortschritt in % – Summe abgeschlossener Lektionen plus echte
     * progress_measure-Werte begonnener Lektionen. Ohne echten Messwert zählt
     * eine begonnene Lektion 0 (kein vorgetäuschter Fortschritt). Passt damit
     * zum „X/Y abgeschlossen" darüber.
     */
    overallPercent(): number {
      if (this.lessons.length === 0) return 0
      const sum = this.lessons.reduce(
        (acc, l) => acc + store.getters['lmsCourse/lessonProgress'](this.moduleId, l.lessonId),
        0,
      )
      return Math.round((sum / this.lessons.length) * 100)
    },
    /** Verbrachte Lernzeit; fällt auf die Katalog-Dauer zurück, solange nichts getrackt wurde */
    spentLabel(): string {
      const seconds = store.getters['lmsCourse/totalSpentSeconds'](this.moduleId)
      if (seconds > 0) return this.formatSpent(seconds)
      return `${this.module?.duration_minutes ?? 0} Min.`
    },
    /** Eyebrow über dem Titel – Kategorie statt technischem Provider-Namen */
    eyebrow(): string {
      return this.module?.category || 'Online-Fortbildung'
    },
    /** Lektion, die der Hero-CTA öffnet: begonnene, sonst nächste offene, sonst erste */
    primaryLesson(): CourseLesson | null {
      if (this.lessons.length === 0) return null
      const inProgress = this.lessons.find((l) => this.isLessonInProgress(l.lessonId))
      if (inProgress) return inProgress
      const next = this.lessons.find((l) => !this.isLessonDone(l.lessonId))
      return next ?? this.lessons[0]
    },
    /** Beschriftung des Hero-CTA je nach Fortschritt */
    ctaLabel(): string {
      if (this.lessons.length === 0) return 'Kurs starten'
      const p = this.primaryLesson
      if (p && this.isLessonInProgress(p.lessonId)) return 'Fortsetzen'
      if (this.completedCount === this.lessons.length) return 'Kurs wiederholen'
      if (this.completedCount > 0) return 'Weiterlernen'
      return 'Kurs starten'
    },
  },

  methods: {
    isLessonDone(lessonId: string): boolean {
      return store.getters['lmsCourse/isLessonCompleted'](this.moduleId, lessonId)
    },
    isLessonInProgress(lessonId: string): boolean {
      return store.getters['lmsCourse/isLessonInProgress'](this.moduleId, lessonId)
    },
    lessonPercent(lessonId: string): number {
      return Math.round(store.getters['lmsCourse/lessonProgress'](this.moduleId, lessonId) * 100)
    },
    hasLessonMeasure(lessonId: string): boolean {
      return store.getters['lmsCourse/hasLessonMeasure'](this.moduleId, lessonId)
    },
    /** Prozentangabe nur zeigen, wenn das Paket einen echten Messwert liefert */
    lessonPercentLabel(lessonId: string): string {
      const sc = store.getters['lmsCourse/getScormState'](this.moduleId, lessonId)
      if (sc && typeof sc.progressMeasure === 'number') return ` · ${this.lessonPercent(lessonId)} %`
      return ''
    },
    lessonButtonLabel(lesson: CourseLesson): string {
      if (this.isLessonDone(lesson.lessonId)) return 'Wiederholen'
      if (this.isLessonInProgress(lesson.lessonId)) return 'Fortsetzen'
      return lesson.s3Key ? 'Starten' : 'Noch nicht verfügbar'
    },
    formatSpent(seconds: number): string {
      if (seconds > 0 && seconds < 60) return '< 1 Min.'
      const min = Math.round(seconds / 60)
      if (min < 60) return `${min} Min.`
      const h = Math.floor(min / 60)
      const m = min % 60
      return m ? `${h} Std. ${m} Min.` : `${h} Std.`
    },
    lessonCompletedAt(lessonId: string): number | null {
      return store.getters['lmsCourse/lessonCompletedAt'](this.moduleId, lessonId)
    },
    formatDate(ts: number | null): string {
      if (!ts) return ''
      return new Date(ts).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    openLesson(lessonId: string) {
      this.$router.push({ name: 'lmsLesson', params: { moduleId: this.moduleId, lessonId } })
    },
    /** Hero-CTA: passende Lektion öffnen bzw. Kurs ohne Lektionen direkt starten */
    startPrimary() {
      if (this.lessons.length === 0) {
        this.startCourseDirect()
        return
      }
      const p = this.primaryLesson
      if (p) this.openLesson(p.lessonId)
    },
    startCourseDirect() {
      // Fallback: Kurs ohne Lektionen direkt starten (alte SCORM-Logik)
      // Öffnet den SCORM-Player mit der course-level lms_path
      this.$router.push({ name: 'lmsLesson', params: { moduleId: this.moduleId, lessonId: '__course' } })
    },
  },

  async mounted() {
    await store.dispatch('lmsCourse/LOAD_CATALOG')
    await Promise.all([
      store.dispatch('lmsCourse/LOAD_PROGRESS', {
        userId: this.userId,
        moduleId: this.moduleId,
      }),
      store.dispatch('lmsCourse/LOAD_LESSONS', this.moduleId),
    ])
    this.loading = false
  },
})
